import { useCallback } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useUploadMutation } from "@/hooks/uploads/useUploadMutation";
import AgentSelection from "../AgentSelection";
import { UploadComponentProps } from "./types";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface UploadUrlProps extends UploadComponentProps {
  pdfUrl: string;
  onPdfUrlChange: (url: string) => void;
}

const UploadUrl = ({
  availableAgents,
  selectedAgentId,
  onAgentSelect,
  pdfUrl,
  onPdfUrlChange,
  onUploadSuccess,
  onDeferredActionAdd,
}: UploadUrlProps) => {
  const extractPdfFromUrlMutation = useUploadMutation("pdf-url", selectedAgentId, onUploadSuccess);

  const handleUrlSubmit = useCallback(async () => {
    if (!pdfUrl) {
      toast.error("Please enter a PDF URL");
      return;
    }

    try {
      new URL(pdfUrl);
    } catch (error) {
      toast.error("Please enter a valid URL");
      return;
    }

    if (onDeferredActionAdd) {
      const urlObj = new URL(pdfUrl);
      const pathSegments = urlObj.pathname.split("/");
      const fileName = pathSegments[pathSegments.length - 1] || "Imported Document";

      onDeferredActionAdd({
        type: "upload-url",
        data: {
          url: pdfUrl,
          fileName: fileName,
        },
      });
      toast.success(`"${fileName}" will be imported after genie creation`);
      onPdfUrlChange("");
    } else {
      extractPdfFromUrlMutation.mutate({
        url: pdfUrl,
      });
      onPdfUrlChange("");
    }
  }, [pdfUrl, extractPdfFromUrlMutation, onPdfUrlChange, onDeferredActionAdd]);

  return (
    <>
      <AgentSelection
        agents={availableAgents}
        selectedAgentId={selectedAgentId}
        onAgentSelect={onAgentSelect}
      />
      <div className="flex flex-col items-center justify-center h-full max-w-2xl mx-auto">
        <GenieIcon name="document" size={64} color="#4E9CFF" gradientEnabled className="mb-4" />
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Extract from PDF URL</h3>
        <p className="text-sm text-gray-600 mb-6 text-center">
          Enter a direct link to a PDF file to extract its content
        </p>

        <div className="flex flex-col sm:flex-row gap-3 w-full">
          <Input
            type="url"
            placeholder="https://example.com/document.pdf"
            value={pdfUrl}
            onChange={(e) => onPdfUrlChange(e.target.value)}
            className="flex-1 h-11 sm:h-12 text-sm sm:text-base"
            disabled={extractPdfFromUrlMutation.isPending}
          />
          <Button
            onClick={handleUrlSubmit}
            disabled={
              extractPdfFromUrlMutation.isPending || !pdfUrl.trim()
            }
            className="w-full sm:w-auto bg-gradient-to-r from-genie-primary to-genie-secondary text-white font-semibold px-6 sm:px-8 h-11 sm:h-12 shadow-lg hover:shadow-xl transition-all hover:scale-105 rounded-xl"
          >
            {extractPdfFromUrlMutation.isPending ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              "Extract"
            )}
          </Button>
        </div>
      </div>
    </>
  );
};

export default UploadUrl;
